/**
 * Agent Node Component - Main orchestrator
 */

import { memo } from 'react'
import { Handle, Position, NodeProps } from '@xyflow/react'
import { toast } from 'sonner'
import { useParams } from 'react-router'
import { useGetProjects } from '@/stores/projectsStore'
import useDataSettings from '@/hooks/useDataSettings'
import NodeBaseContainer from '@/components/nodes/base/base-container/NodeBaseContainer'
import NodeDataSettingsDialog from '@/components/dialogs/data-settings-dialog/NodeDataSettingsDialog'
import AgentLogsDialog from '@/components/nodes/agent/components/AgentLogsDialog'
import AgentUsageDialog from '@/components/nodes/agent/components/AgentUsageDialog'
import AgentCollaborationDialog from '@/components/nodes/agent/components/AgentCollaborationDialog'
import AgentQdrantDialog from '@/components/nodes/agent/components/AgentQdrantDialog'
import AgentSchedulingDialog from '@/components/nodes/agent/components/AgentSchedulingDialog'
import { useAgentNodeState } from './hooks/useAgentNodeState'
import { AgentContent } from './components/AgentContent'
import { AgentSettingsDialog } from './components/AgentSettingsDialog'
import { AgentManualTaskDialog } from './components/AgentManualTaskDialog'
import { AgentNodeData } from './types'

const NodeAgent = ({ id, data, selected }: NodeProps & { data: AgentNodeData }) => {  const { projectId } = useParams()
  const { projects } = useGetProjects()
  const currentProject = projects?.[0] // Simplified for now  // Use the custom hook for all state management
  const {
    // State
    isLocked,
    isMinimized,
    minimizeLevel,
    isEditingTitle,
    editingTitleValue,
    manualTaskInstructions,
    selectedNodes,
    dialogs,
    avatarGenerating,
    avatarUploading,
    
    // Computed values
    logs,
    tasks,
    nodeStatus,
    fileInputRef,
    
    // State updaters
    updateState,
    updateDialogState,
    
    // Handlers
    handleLock,
    handleMinimize,
    handleMinimizeLevelChange,
    handleCreateAgent,
    handleUpdateAgent,
    handleGenerateAvatar,
    handleAvatarUpload,
  } = useAgentNodeState(id, data)

  // Data Settings for node connections
  const outgoingFields = {
    agentId: {
      value: data.agentId || '',
      enabled: !!data.agentId,
      label: 'Agent ID',
      description: 'Unique identifier for this agent',
      permissions: { readable: true, writable: false },
    },
    name: {
      value: data.name || '',
      enabled: true,
      label: 'Agent Name',
      description: 'Display name of the agent',
      permissions: { readable: true, writable: false },
    },
    role: {
      value: data.role || '',
      enabled: true,
      label: 'Agent Role',
      description: 'Role/profession of the agent',
      permissions: { readable: true, writable: false },
    },
    enabled: {
      value: data.enabled || false,
      enabled: true,
      label: 'Agent Enabled',
      description: 'Whether the agent is currently active',
      permissions: { readable: true, writable: false },
    },
    observationList: {
      value: data.observationList || [],
      enabled: true,
      label: 'Observed Nodes',
      description: 'List of node IDs this agent is observing',
      permissions: { readable: true, writable: false },
    },
  }

  const {
    dataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
    handleOpenDataSettingsDialog,
  } = useDataSettings(
    id,
    data,
    (_newData) => {
      // Update handled by the hook
    },
    false,
    '',
    new Map(),
    '',
    (visible) => updateDialogState({ isDataSettingsDialogVisible: visible })
  )

  // Agent-specific handlers
  const handleToggleAgent = async () => {
    if (!data.agentId) return
    try {
      await handleUpdateAgent({ enabled: !data.enabled })
    } catch (error) {
      toast.error('Failed to toggle agent')
    }
  }

  const handleExecuteManualTask = async () => {
    if (!manualTaskInstructions.trim()) {
      toast.error('Please provide task instructions')
      return
    }

    if (!currentProject?.id) {
      toast.error('No project selected')
      return
    }

    try {
      // Create agent command similar to documentCommand pattern
      const agentCommand = {
        'sysagent-cmd': 'NodeAgent-Execute-Manual-Task-Started',
        'sysagent-response': '',
        mode: 'manual-task',
        processing: true,
        message: 'Executing manual task...',
        taskType: 'manual',
        instructions: manualTaskInstructions,
        targetNodeIds: selectedNodes,
      }

      // Update node data with agent command
      handleUpdateAgent({
        agentCommand,
        statusMessage: 'Executing manual task...',
      })

      updateDialogState({ showManualTask: false })
      updateState({ 
        manualTaskInstructions: '',
        selectedNodes: [],
      })
      toast.success('Manual task started')
    } catch (error) {
      toast.error('Failed to start manual task')
    }
  }

  const effectiveProjectId = projectId || currentProject?.id || 'temp-project-id'

  return (
    <>      <NodeBaseContainer
        id={id}
        selected={selected}
        isLocked={isLocked}
        isMinimized={isMinimized}
        minimizeLevel={minimizeLevel}
        onLockChange={handleLock}
        onMinimizeChange={handleMinimize}
        onMinimizeLevelChange={handleMinimizeLevelChange}
        onTitleChange={(value) => updateState({ editingTitleValue: value })}
        className="min-w-[280px]"
      >
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 !bg-orange-400"
        />
        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 !bg-orange-400"
        />

        <AgentContent
          data={data}
          state={{
            isLocked,
            isMinimized,
            minimizeLevel,
            isEditingTitle,
            editingTitleValue,
            manualTaskInstructions,
            selectedNodes,
            agentLogs: logs,
            agentTasks: tasks,
            avatarGenerating,
            avatarUploading,
            dialogs,
          }}
          nodeStatus={nodeStatus}
          onCreateAgent={handleCreateAgent}
          onToggleAgent={handleToggleAgent}
          onShowSettings={() => updateDialogState({ showAgentSettings: true })}
          onShowLogs={() => updateDialogState({ showAgentLogs: true })}
          onShowUsage={() => updateDialogState({ showAgentUsage: true })}
          onShowCollaboration={() => updateDialogState({ showAgentCollaboration: true })}
          onShowQdrant={() => updateDialogState({ showAgentQdrant: true })}
          onShowScheduling={() => updateDialogState({ showAgentScheduling: true })}
          onShowManualTask={() => updateDialogState({ showManualTask: true })}
          onGenerateAvatar={handleGenerateAvatar}
          onAvatarUpload={handleAvatarUpload}
          onOpenDataSettings={handleOpenDataSettingsDialog}
          fileInputRef={fileInputRef}
        />
      </NodeBaseContainer>

      {/* Agent Settings Dialog */}
      <AgentSettingsDialog
        open={dialogs.showAgentSettings}
        onOpenChange={(open) => updateDialogState({ showAgentSettings: open })}
        data={data}
        onUpdateAgent={handleUpdateAgent}
      />

      {/* Manual Task Dialog */}
      <AgentManualTaskDialog
        open={dialogs.showManualTask}
        onOpenChange={(open) => updateDialogState({ showManualTask: open })}
        instructions={manualTaskInstructions}
        onInstructionsChange={(instructions) => updateState({ manualTaskInstructions: instructions })}
        selectedNodes={selectedNodes}
        onSelectedNodesChange={(nodes) => updateState({ selectedNodes: nodes })}
        onExecuteTask={handleExecuteManualTask}
      />

      {/* Agent Logs Dialog */}
      <AgentLogsDialog
        isOpen={dialogs.showAgentLogs}
        onClose={() => updateDialogState({ showAgentLogs: false })}
        agentId={data.agentId || ''}
        agentName={data.name || 'Agent'}
        projectId={effectiveProjectId}
      />

      {/* Data Settings Dialog */}
      <NodeDataSettingsDialog
        visible={dialogs.isDataSettingsDialogVisible}
        onHide={() => updateDialogState({ isDataSettingsDialogVisible: false })}
        settings={dataSettings || { inputLinks: [], outputLinks: [], exposedData: outgoingFields, availableNodes: [], autoSync: false }}
        nodeId={id}
        onSettingsChange={handleDataSettingsChange}
        onRemoveInputLink={handleRemoveInputLink}
        onSyncInputLink={handleSyncInputLink}
        forceShowAvailableNodes={true}
        debug={false}
      />

      {/* Agent Usage Dialog */}
      <AgentUsageDialog
        isOpen={dialogs.showAgentUsage}
        onClose={() => updateDialogState({ showAgentUsage: false })}
        agentId={data.agentId || ''}
        agentName={data.name || 'Agent'}
        projectId={effectiveProjectId}
      />

      {/* Agent Collaboration Dialog */}
      <AgentCollaborationDialog
        isOpen={dialogs.showAgentCollaboration}
        onClose={() => updateDialogState({ showAgentCollaboration: false })}
        agentId={data.agentId || ''}
        agentName={data.name || 'Agent'}
        projectId={effectiveProjectId}
      />

      {/* Agent Qdrant Dialog */}
      <AgentQdrantDialog
        isOpen={dialogs.showAgentQdrant}
        onClose={() => updateDialogState({ showAgentQdrant: false })}
        agent={{
          id: data.agentId || 'temp-agent-id',
          name: data.name || 'Agent',
          role: data.role || 'general',
          type: data.type || 'assistant',
          enabled: data.enabled || false,
          avatarUrl: data.avatarUrl || '',
          instructions: data.instructions || '',
          triggerEvents: data.triggerEvents || [],
          observationList: data.observationList || [],
          settings: data.settings || {},
          projectId: effectiveProjectId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }}
      />

      {/* Agent Scheduling Dialog */}
      <AgentSchedulingDialog
        isOpen={dialogs.showAgentScheduling}
        onClose={() => updateDialogState({ showAgentScheduling: false })}
        agent={{
          id: data.agentId || 'temp-agent-id',
          name: data.name || 'Agent',
          role: data.role || 'general',
          type: data.type || 'assistant',
          enabled: data.enabled || false,
          avatarUrl: data.avatarUrl || '',
          instructions: data.instructions || '',
          triggerEvents: data.triggerEvents || [],
          observationList: data.observationList || [],
          settings: data.settings || {},
          projectId: effectiveProjectId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }}
      />
    </>
  )
}

export default memo(NodeAgent)
